import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Switch, Text, useDisclosure } from '@bonusly/design-system';

import { useSeasonalTheme } from './seasonal-theme-context';
import { SeasonalThemeModal } from './seasonal-theme-modal';

interface SeasonalToggleProps {
	isExpanded?: boolean;
}

export const SeasonalToggle: FC<SeasonalToggleProps> = ({ isExpanded = true }) => {
	const { t } = useTranslation('components');
	const { hasSeasonalThemePermission, isSeasonalThemeEnabled, toggleTheme } = useSeasonalTheme();
	const { isOpen, onOpen, onClose } = useDisclosure();

	// Only show toggle if user has permission (seasonal-themes feature flag) and sidebar is expanded
	if (!hasSeasonalThemePermission || !isExpanded) {
		return null;
	}

	const handleToggle = () => {
		if (isSeasonalThemeEnabled) {
			// If enabled, just toggle it off
			toggleTheme();
		} else {
			// If disabled, show the modal first
			onOpen();
		}
	};

	const labelId = 'seasonal-theme-label';

	return (
		<>
			<Flex
				as="label"
				htmlFor="seasonal-theme-toggle"
				w="100%"
				alignItems="center"
				justifyContent="space-between"
				py={2}
				px={2.5}
				borderRadius="md"
				bg={isSeasonalThemeEnabled ? 'primary.bgAlpha' : 'transparent'}
				cursor="pointer"
				opacity={isExpanded ? 1 : 0}
				transition="all 0.15s cubic-bezier(.4, 0, .2, 1)"
				_hover={{
					bg: isSeasonalThemeEnabled ? 'primary.bgAlphaHover' : 'secondary.bgAlpha',
				}}
				_focusWithin={{ outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '-2px' }}
			>
				<Flex alignItems="center" gap={3}>
					<Text fontSize="md" lineHeight={1} aria-hidden="true">
						❄️
					</Text>
					<Text id={labelId} fontSize={100} fontWeight="medium" whiteSpace="nowrap" userSelect="none">
						{t('seasonal_theme.toggle.label', { defaultValue: 'Holiday theme' })}
					</Text>
				</Flex>
				<Switch
					id="seasonal-theme-toggle"
					size="sm"
					isChecked={isSeasonalThemeEnabled}
					onChange={handleToggle}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							handleToggle();
						}
					}}
					colorScheme="blue"
					aria-labelledby={labelId}
					sx={{
						'& .chakra-switch__track': {
							_focusVisible: {
								boxShadow: 'none',
							},
						},
					}}
				/>
			</Flex>

			<SeasonalThemeModal isOpen={isOpen} onClose={onClose} onEnable={toggleTheme} />
		</>
	);
};
